import { DISCOVERY_PAGE_KEY } from "@next-platform/packages-ts-types";
import { useMemo } from "react";
import { useAppSelector } from "../hook";
import useUserInfo from "../user/useUserInfo";
import discoveryCandidateAccessors from "./discoveryCandidateAccessors";
import holdResolvedRows from "./holdResolvedRows";
import { areDiscoveryRecommendationFiltersReady } from "./loadDiscoveryRecommendationRow";
import publishRows from "./publishRows";
import usePageController from "./usePageController";
/**
 * Shared Web/Native controller for the Discovery page. The caller provides the
 * effective NSFW preference and its recommendation loader; everything else is
 * derived from the already-loaded application settings.
 */
export default function useDiscoveryPageController({ isNsfw, loadRecommendations, }) {
    const viewModel = usePageController({ pageKey: DISCOVERY_PAGE_KEY, isNsfw, loadRecommendations });
    const { isAuthenticated } = useUserInfo();
    const blockedUsers = useAppSelector((state) => state.userSettings.blocked_users);
    const isUserSettingsLoaded = useAppSelector((state) => state.userSettings.are_user_settings_loaded);
    const settingsFetchStatus = useAppSelector((state) => state.userSettings.are_user_settings_loading);
    const blockedCreatorIds = useMemo(() => new Set(blockedUsers), [blockedUsers]);
    const isViewerFilterReady = areDiscoveryRecommendationFiltersReady({
        isAuthenticated: Boolean(isAuthenticated),
        isUserSettingsLoaded,
        settingsFetchStatus,
    });
    const rawRows = viewModel.status === "ready" ? viewModel.rows : null;
    const rows = useMemo(() => {
        if (rawRows === null)
            return [];
        if (!isViewerFilterReady)
            return holdResolvedRows(rawRows);
        return publishRows(rawRows, blockedCreatorIds, discoveryCandidateAccessors);
    }, [rawRows, blockedCreatorIds, isViewerFilterReady]);
    if (viewModel.status !== "ready")
        return viewModel;
    return { ...viewModel, rows };
}
